import React, { useState, useEffect, useCallback } from "react";
import "./UserList.css";
import api from "../services/api";

interface User {
  _id: string;
  name: string;
  phone: string;
  province: string;
  role: "megaadmin" | "super_admin" | "admin" | "receptionniste" | "protocole" | "organisateur";
  createdBy?: string | null;
  creatorDetails?: {
    _id: string;
    name: string;
    role: string;
  } | null;
  mariagesAssignes: MariageSimple[];
  evenementsAssignes: EvenementSimple[];
  anniversairesAssignes: AnniversaireSimple[];
}

interface MariageSimple {
  _id: string;
  bride: string;
  groom: string;
  date?: string;
  dateISO?: string;
}

interface EvenementSimple {
  _id: string;
  type: string;
  dateISO: string;
  lieu?: string;
}

interface AnniversaireSimple {
  _id: string;
  personne: string;
  age: number;
  dateISO: string;
}

interface Mariage {
  _id: string;
  bride: string;
  groom: string;
  date: string;
  dateISO: string;
  province: string;
}

interface Evenement {
  _id: string;
  type: string;
  dateISO: string;
  heureISO: string;
  lieu?: string;
  province: string;
}

interface Anniversaire {
  _id: string;
  personne: string;
  age: number;
  dateISO: string;
  heureISO: string;
  lieu: string;
  province: string;
}

interface FormData {
  name: string;
  phone: string;
  password: string;
  province: string;
  role: "megaadmin" | "super_admin" | "admin" | "receptionniste" | "protocole" | "organisateur";
  mariagesAssignes: string[];
  evenementsAssignes: string[];
  anniversairesAssignes: string[];
}

interface CurrentUser {
  _id: string;
  role: string;
  province: string;
  name: string;
}

interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  [key: string]: any;
}

interface AuthResponse {
  success: boolean;
  user: CurrentUser;
}

interface ProvincesResponse {
  success: boolean;
  provinces: string[];
}

interface UsersResponse {
  success: boolean;
  users: User[];
}

interface UserResponse {
  success: boolean;
  user: User;
}

interface MariagesResponse {
  success: boolean;
  mariages: Mariage[];
}

interface EvenementsResponse {
  success: boolean;
  evenements: Evenement[];
}

interface AnniversairesResponse {
  success: boolean;
  anniversaires: Anniversaire[];
}

const UserList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [mariages, setMariages] = useState<Mariage[]>([]);
  const [evenements, setEvenements] = useState<Evenement[]>([]);
  const [anniversaires, setAnniversaires] = useState<Anniversaire[]>([]);
  const [provinces, setProvinces] = useState<string[]>([]);
  const [filterProvince, setFilterProvince] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [activeAssignTab, setActiveAssignTab] = useState<"mariages" | "evenements" | "anniversaires">("mariages");
  
  const [formData, setFormData] = useState<FormData>({
    name: "",
    phone: "",
    password: "",
    province: "",
    role: "receptionniste",
    mariagesAssignes: [],
    evenementsAssignes: [],
    anniversairesAssignes: []
  });

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await api.get<AuthResponse>("/auth/me");
      if (response.data.success) {
        setCurrentUser(response.data.user);
        if (response.data.user.role === "admin") {
          setFilterProvince(response.data.user.province);
        }
      }
    } catch (error) {
      console.error("Erreur chargement utilisateur:", error);
    }
  }, []);

  const fetchProvinces = useCallback(async () => {
    try {
      const response = await api.get<ProvincesResponse>("/admin/users/provinces");
      if (response.data.success) {
        setProvinces(response.data.provinces);
      }
    } catch (error) {
      console.error("Erreur chargement provinces:", error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterProvince) params.append("province", filterProvince);
      
      const response = await api.get<UsersResponse>(`/admin/users?${params.toString()}`);
      
      if (response.data.success) {
        const usersWithCreators = await Promise.all(
          response.data.users.map(async (user: User) => {
            if (user.createdBy) {
              try {
                const creatorResponse = await api.get<UserResponse>(`/admin/users/${user.createdBy}`);
                if (creatorResponse.data.success) {
                  user.creatorDetails = {
                    _id: creatorResponse.data.user._id,
                    name: creatorResponse.data.user.name,
                    role: creatorResponse.data.user.role
                  };
                }
              } catch (error) {
                console.error("Erreur chargement créateur:", error);
              }
            }
            return user;
          })
        );
        
        setUsers(usersWithCreators);
      }
    } catch (error) {
      console.error("Erreur chargement users:", error);
    } finally {
      setLoading(false);
    }
  }, [filterProvince]);

  const fetchMariages = useCallback(async () => {
    try {
      const response = await api.get<MariagesResponse>("/mariages");
      if (response.data.success && Array.isArray(response.data.mariages)) {
        setMariages(response.data.mariages);
      }
    } catch (error) {
      console.error("Erreur chargement mariages:", error);
    }
  }, []);

  const fetchEvenements = useCallback(async () => {
    try {
      const response = await api.get<EvenementsResponse>("/evenements");
      if (response.data.success && Array.isArray(response.data.evenements)) {
        setEvenements(response.data.evenements);
      }
    } catch (error) {
      console.error("Erreur chargement événements:", error);
    }
  }, []);

  const fetchAnniversaires = useCallback(async () => {
    try {
      const response = await api.get<AnniversairesResponse>("/anniversaires");
      if (response.data.success && Array.isArray(response.data.anniversaires)) {
        setAnniversaires(response.data.anniversaires);
      }
    } catch (error) {
      console.error("Erreur chargement anniversaires:", error);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
    fetchProvinces();
    fetchUsers();
    fetchMariages();
    fetchEvenements();
    fetchAnniversaires();
  }, [fetchCurrentUser, fetchProvinces, fetchUsers, fetchMariages, fetchEvenements, fetchAnniversaires]);

  useEffect(() => {
    fetchUsers();
  }, [filterProvince, fetchUsers]);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "Date non définie";
    
    try {
      if (dateString.includes('-')) {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('fr-FR');
        }
      }
      
      const moisMap: { [key: string]: number } = {
        'janvier': 0, 'février': 1, 'mars': 2, 'avril': 3,
        'mai': 4, 'juin': 5, 'juillet': 6, 'août': 7,
        'septembre': 8, 'octobre': 9, 'novembre': 10, 'décembre': 11
      };
      
      const mots = dateString.toLowerCase().split(' ');
      const jour = parseInt(mots[0]);
      const moisNom = mots[1];
      const annee = parseInt(mots[2]);
      
      if (moisMap[moisNom] !== undefined && !isNaN(jour) && !isNaN(annee)) {
        const date = new Date(annee, moisMap[moisNom], jour);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('fr-FR');
        }
      }
      
      return "Date invalide";
    } catch {
      return "Date invalide";
    }
  };

  const getExistingMariagesForUser = (user: User) => {
    const mariagesExistantsIds = mariages.map(m => m._id);
    return user.mariagesAssignes.filter(m => mariagesExistantsIds.includes(m._id));
  };

  const getExistingEvenementsForUser = (user: User) => {
    const evenementsExistantsIds = evenements.map(e => e._id);
    return user.evenementsAssignes?.filter(e => evenementsExistantsIds.includes(e._id)) || [];
  };

  const getExistingAnniversairesForUser = (user: User) => {
    const anniversairesExistantsIds = anniversaires.map(a => a._id);
    return user.anniversairesAssignes?.filter(a => anniversairesExistantsIds.includes(a._id)) || [];
  };

  const getDeletedMariagesCount = (user: User) => {
    const mariagesExistantsIds = mariages.map(m => m._id);
    return user.mariagesAssignes.filter(m => !mariagesExistantsIds.includes(m._id)).length;
  };

  const getDeletedEvenementsCount = (user: User) => {
    const evenementsExistantsIds = evenements.map(e => e._id);
    return user.evenementsAssignes?.filter(e => !evenementsExistantsIds.includes(e._id)).length || 0;
  };

  const getDeletedAnniversairesCount = (user: User) => {
    const anniversairesExistantsIds = anniversaires.map(a => a._id);
    return user.anniversairesAssignes?.filter(a => !anniversairesExistantsIds.includes(a._id)).length || 0;
  };

  const canModifyUser = (user: User) => {
    if (!currentUser) return false;
    
    if (currentUser.role === "megaadmin") {
      return true;
    }
    
    if (currentUser.role === "super_admin") {
      return ["admin", "receptionniste", "protocole", "organisateur"].includes(user.role);
    }
    
    if (currentUser.role === "admin") {
      return (user.role === "receptionniste" || user.role === "protocole") && 
             user.createdBy === currentUser._id;
    }
    
    return false;
  };

  const canSeeUser = (user: User) => {
    if (!currentUser) return false;
    
    if (currentUser.role === "megaadmin") {
      return true;
    }
    
    if (currentUser.role === "super_admin") {
      return ["admin", "receptionniste", "protocole", "organisateur"].includes(user.role);
    }
    
    if (currentUser.role === "admin") {
      return (user.role === "receptionniste" || user.role === "protocole") && 
             user.province === currentUser.province;
    }
    
    return false;
  };

  const canSeePhone = (user: User) => {
    if (!currentUser) return false;
    
    if (currentUser.role === "megaadmin") return true;
    if (currentUser.role === "super_admin") return true;
    
    if (currentUser.role === "admin") {
      return user.createdBy === currentUser._id;
    }
    
    return false;
  };

  const maskPhone = (phone: string) => {
    if (!phone) return "";
    const visiblePart = phone.slice(0, 5);
    return visiblePart + "********";
  };

  const getCreatorLabel = (user: User) => {
    if (!user.createdBy) return "Créé par Super Admin";
    if (user.creatorDetails) {
      return `Créé par ${user.creatorDetails.name} (${user.creatorDetails.role})`;
    }
    return "Créé par Admin";
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMariageToggle = (mariageId: string) => {
    setFormData(prev => {
      const isSelected = prev.mariagesAssignes.includes(mariageId);
      return {
        ...prev,
        mariagesAssignes: isSelected
          ? prev.mariagesAssignes.filter(id => id !== mariageId)
          : [...prev.mariagesAssignes, mariageId]
      };
    });
  };

  const handleEvenementToggle = (evenementId: string) => {
    setFormData(prev => {
      const isSelected = prev.evenementsAssignes.includes(evenementId);
      return {
        ...prev,
        evenementsAssignes: isSelected
          ? prev.evenementsAssignes.filter(id => id !== evenementId)
          : [...prev.evenementsAssignes, evenementId]
      };
    });
  };

  const handleAnniversaireToggle = (anniversaireId: string) => {
    setFormData(prev => {
      const isSelected = prev.anniversairesAssignes.includes(anniversaireId);
      return {
        ...prev,
        anniversairesAssignes: isSelected
          ? prev.anniversairesAssignes.filter(id => id !== anniversaireId)
          : [...prev.anniversairesAssignes, anniversaireId]
      };
    });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      phone: "",
      password: "",
      province: currentUser?.role === "admin" ? currentUser.province : "",
      role: "receptionniste",
      mariagesAssignes: [],
      evenementsAssignes: [],
      anniversairesAssignes: []
    });
    setEditingUser(null);
    setActiveAssignTab("mariages");
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (user: User) => {
    if (!canModifyUser(user)) {
      alert("Vous n'avez pas les droits pour modifier cet utilisateur");
      return;
    }
    setEditingUser(user);
    const existingMariages = getExistingMariagesForUser(user);
    const existingEvenements = getExistingEvenementsForUser(user);
    const existingAnniversaires = getExistingAnniversairesForUser(user);
    setFormData({
      name: user.name,
      phone: user.phone,
      password: "",
      province: user.province,
      role: user.role,
      mariagesAssignes: existingMariages.map(m => m._id),
      evenementsAssignes: existingEvenements.map(e => e._id),
      anniversairesAssignes: existingAnniversaires.map(a => a._id)
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const url = editingUser
      ? `/admin/users/${editingUser._id}`
      : "/admin/users";
    
    const method = editingUser ? "PUT" : "POST";
    
    let dataToSend: any = { ...formData };
    
    if (editingUser && !dataToSend.password) {
      const { password, ...dataWithoutPassword } = dataToSend;
      dataToSend = dataWithoutPassword;
    }
    
    try {
      let response;
      if (method === "PUT") {
        response = await api.put<ApiResponse>(url, dataToSend);
      } else {
        response = await api.post<ApiResponse>(url, dataToSend);
      }
      
      if (response.data.success) {
        alert(editingUser ? "Utilisateur modifié !" : "Utilisateur créé !");
        setShowModal(false);
        resetForm();
        fetchUsers();
      } else {
        alert(response.data.message || "Erreur");
      }
    } catch (error: any) {
      console.error("Erreur:", error);
      alert(error.response?.data?.message || "Erreur lors de l'enregistrement");
    }
  };

  const handleDelete = async (userId: string) => {
    const userToDelete = users.find(u => u._id === userId);
    if (!userToDelete) return;
    
    if (!canModifyUser(userToDelete)) {
      alert("Vous n'avez pas les droits pour supprimer cet utilisateur");
      return;
    }
    
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    
    try {
      const response = await api.delete<ApiResponse>(`/admin/users/${userId}`);
      
      if (response.data.success) {
        alert("Utilisateur supprimé");
        fetchUsers();
      } else {
        alert(response.data.message || "Erreur");
      }
    } catch (error: any) {
      console.error("Erreur:", error);
      alert(error.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  const getEvenementTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      bapteme: "Baptême",
      fete: "Fête",
      conference: "Conférence",
      autre: "Autre"
    };
    return labels[type] || type;
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  const filteredUsers = users.filter(user => canSeeUser(user));

  return (
    <div className="userlist-container">
      <div className="userlist-header">
        <h3>Gestion des utilisateurs</h3>
        <div className="header-actions">
          {(currentUser?.role === "super_admin" || currentUser?.role === "megaadmin") && (
            <select
              value={filterProvince}
              onChange={(e) => setFilterProvince(e.target.value)}
              className="province-filter"
            >
              <option value="">Toutes les provinces</option>
              {provinces.map(prov => (
                <option key={prov} value={prov}>{prov}</option>
              ))}
            </select>
          )}
          <button className="btn-create" onClick={openCreateModal}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
            </svg>
            Nouvel utilisateur
          </button>
        </div>
      </div>

      <div className="userlist-grid">
        {filteredUsers.length === 0 ? (
          <p className="empty-message">Aucun utilisateur à afficher</p>
        ) : (
          filteredUsers.map(user => {
            const existingMariages = getExistingMariagesForUser(user);
            const existingEvenements = getExistingEvenementsForUser(user);
            const existingAnniversaires = getExistingAnniversairesForUser(user);
            const deletedMariagesCount = getDeletedMariagesCount(user);
            const deletedEvenementsCount = getDeletedEvenementsCount(user);
            const deletedAnniversairesCount = getDeletedAnniversairesCount(user);
            const canModify = canModifyUser(user);
            const seePhone = canSeePhone(user);
            
            return (
              <div key={user._id} className={`user-card ${!canModify ? 'readonly' : ''}`}>
                <div className="user-card-header">
                  <h4>{user.name}</h4>
                  <span className={`role-badge ${user.role}`}>
                    {user.role === "megaadmin" && "Mega Admin"}
                    {user.role === "super_admin" && "Super Admin"}
                    {user.role === "admin" && "Admin"}
                    {user.role === "receptionniste" && "Réceptionniste"}
                    {user.role === "protocole" && "Protocole"}
                    {user.role === "organisateur" && "Organisateur"}
                  </span>
                </div>
                
                <div className="user-card-body">
                  <p>
                    <strong>Tél:</strong> {seePhone ? user.phone : maskPhone(user.phone)}
                  </p>
                  {!seePhone && (
                    <p className="phone-masked-hint">Masqué</p>
                  )}
                  
                  <p>
                    <strong>Province:</strong> {user.province}
                  </p>

                  {(currentUser?.role === "megaadmin" || currentUser?.role === "super_admin") && user.createdBy && (
                    <p className="creator-info">
                      {getCreatorLabel(user)}
                    </p>
                  )}
                  
                  {!canModify && currentUser?.role === "admin" && (
                    <p className="readonly-badge">Lecture seule</p>
                  )}
                  
                  <p><strong>Mariages assignés:</strong></p>
                  {existingMariages.length === 0 ? (
                    <p className="no-data">Aucun mariage</p>
                  ) : (
                    <>
                      <ul className="mariage-list">
                        {existingMariages.map(m => (
                          <li key={m._id}>
                            {m.bride} & {m.groom} - {formatDate(m.dateISO || m.date)}
                          </li>
                        ))}
                      </ul>
                      {deletedMariagesCount > 0 && (
                        <p className="warning-text">
                          {deletedMariagesCount} mariage(s) supprimé(s) (seront retirés à la modification)
                        </p>
                      )}
                    </>
                  )}

                  <p><strong>Événements assignés:</strong></p>
                  {existingEvenements.length === 0 ? (
                    <p className="no-data">Aucun événement</p>
                  ) : (
                    <>
                      <ul className="mariage-list">
                        {existingEvenements.map(e => (
                          <li key={e._id}>
                            {getEvenementTypeLabel(e.type)} - {formatDate(e.dateISO)}
                            {e.lieu && ` - ${e.lieu}`}
                          </li>
                        ))}
                      </ul>
                      {deletedEvenementsCount > 0 && (
                        <p className="warning-text">
                          {deletedEvenementsCount} événement(s) supprimé(s) (seront retirés à la modification)
                        </p>
                      )}
                    </>
                  )}

                  <p><strong>Anniversaires assignés:</strong></p>
                  {existingAnniversaires.length === 0 ? (
                    <p className="no-data">Aucun anniversaire</p>
                  ) : (
                    <>
                      <ul className="mariage-list">
                        {existingAnniversaires.map(a => (
                          <li key={a._id}>
                            {a.personne} ({a.age} ans) - {formatDate(a.dateISO)}
                          </li>
                        ))}
                      </ul>
                      {deletedAnniversairesCount > 0 && (
                        <p className="warning-text">
                          {deletedAnniversairesCount} anniversaire(s) supprimé(s) (seront retirés à la modification)
                        </p>
                      )}
                    </>
                  )}
                </div>
                
                <div className="user-card-footer">
                  <button 
                    className={`btn-edit ${!canModify ? 'disabled' : ''}`}
                    onClick={() => openEditModal(user)}
                    disabled={!canModify}
                  >
                    Modifier
                  </button>
                  <button 
                    className={`btn-delete ${!canModify ? 'disabled' : ''}`}
                    onClick={() => handleDelete(user._id)}
                    disabled={!canModify}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-large">
            <h3>{editingUser ? "Modifier" : "Créer"} un utilisateur</h3>
            
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Nom complet *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Téléphone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+243"
                    required
                  />
                  <small>Ce sera son identifiant de connexion</small>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Province *</label>
                  <select
                    name="province"
                    value={formData.province}
                    onChange={handleInputChange}
                    required
                    className="form-control"
                    disabled={currentUser?.role === "admin"}
                  >
                    {currentUser?.role === "admin" ? (
                      <option value={currentUser.province}>{currentUser.province}</option>
                    ) : (
                      <>
                        <option value="">Sélectionnez une province</option>
                        {provinces.map(prov => (
                          <option key={prov} value={prov}>{prov}</option>
                        ))}
                      </>
                    )}
                  </select>
                  {currentUser?.role === "admin" && (
                    <small>Vous ne pouvez créer que dans votre province</small>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Mot de passe {editingUser && "(laisser vide pour ne pas changer)"} *</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={!editingUser}
                  />
                  {editingUser && (
                    <small>Laissez vide pour conserver l'ancien mot de passe</small>
                  )}
                </div>
              </div>
              
              <div className="form-group">
                <label>Rôle *</label>
                <select name="role" value={formData.role} onChange={handleInputChange}>
                  <option value="receptionniste">Réceptionniste</option>
                  <option value="protocole">Protocole</option>
                  <option value="organisateur">Organisateur</option>
                  
                  {(currentUser?.role === "super_admin" || currentUser?.role === "megaadmin") && (
                    <option value="admin">Admin</option>
                  )}
                  
                  {currentUser?.role === "megaadmin" && (
                    <option value="super_admin">Super Admin</option>
                  )}
                </select>
                {currentUser?.role === "admin" && (
                  <small className="hint">Vous ne pouvez créer que des réceptionnistes et protocoles</small>
                )}
              </div>
              
              <div className="assign-tabs">
                <button
                  type="button"
                  className={`assign-tab ${activeAssignTab === "mariages" ? "active" : ""}`}
                  onClick={() => setActiveAssignTab("mariages")}
                >
                  Mariages ({mariages.filter(m => currentUser?.role === "admin" ? m.province === currentUser.province : true).length})
                </button>
                <button
                  type="button"
                  className={`assign-tab ${activeAssignTab === "evenements" ? "active" : ""}`}
                  onClick={() => setActiveAssignTab("evenements")}
                >
                  Événements ({evenements.filter(e => currentUser?.role === "admin" ? e.province === currentUser.province : true).length})
                </button>
                <button
                  type="button"
                  className={`assign-tab ${activeAssignTab === "anniversaires" ? "active" : ""}`}
                  onClick={() => setActiveAssignTab("anniversaires")}
                >
                  Anniversaires ({anniversaires.filter(a => currentUser?.role === "admin" ? a.province === currentUser.province : true).length})
                </button>
              </div>
              
              {activeAssignTab === "mariages" && (
                <div className="form-group">
                  <label>Mariages assignés</label>
                  <div className="mariages-checkbox-list">
                    {mariages.length === 0 ? (
                      <p className="no-data">Aucun mariage disponible</p>
                    ) : (
                      mariages
                        .filter(m => currentUser?.role === "admin" ? m.province === currentUser.province : true)
                        .map(m => (
                          <label key={m._id} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={formData.mariagesAssignes.includes(m._id)}
                              onChange={() => handleMariageToggle(m._id)}
                            />
                            <span className="mariage-info">
                              {m.bride} & {m.groom} - {formatDate(m.dateISO || m.date)}
                              <span className="province-badge">{m.province}</span>
                            </span>
                          </label>
                        ))
                    )}
                  </div>
                </div>
              )}
              
              {activeAssignTab === "evenements" && (
                <div className="form-group">
                  <label>Événements assignés</label>
                  <div className="mariages-checkbox-list">
                    {evenements.length === 0 ? (
                      <p className="no-data">Aucun événement disponible</p>
                    ) : (
                      evenements
                        .filter(e => currentUser?.role === "admin" ? e.province === currentUser.province : true)
                        .map(e => (
                          <label key={e._id} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={formData.evenementsAssignes.includes(e._id)}
                              onChange={() => handleEvenementToggle(e._id)}
                            />
                            <span className="mariage-info">
                              {getEvenementTypeLabel(e.type)} - {formatDate(e.dateISO)}
                              {e.lieu && ` - ${e.lieu}`}
                              <span className="province-badge">{e.province}</span>
                            </span>
                          </label>
                        ))
                    )}
                  </div>
                </div>
              )}
              
              {activeAssignTab === "anniversaires" && (
                <div className="form-group">
                  <label>Anniversaires assignés</label>
                  <div className="mariages-checkbox-list">
                    {anniversaires.length === 0 ? (
                      <p className="no-data">Aucun anniversaire disponible</p>
                    ) : (
                      anniversaires
                        .filter(a => currentUser?.role === "admin" ? a.province === currentUser.province : true)
                        .map(a => (
                          <label key={a._id} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={formData.anniversairesAssignes.includes(a._id)}
                              onChange={() => handleAnniversaireToggle(a._id)}
                            />
                            <span className="mariage-info">
                              {a.personne} ({a.age} ans) - {formatDate(a.dateISO)}
                              <span className="province-badge">{a.province}</span>
                            </span>
                          </label>
                        ))
                    )}
                  </div>
                </div>
              )}
              
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-save">
                  {editingUser ? "Modifier" : "Créer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
  
};

export default UserList;